import { useEffect, useRef, useState } from 'react';
import { Application } from '@ovh-ux/manager-config';
import { fetchFeatureAvailabilityData } from '@ovh-ux/manager-react-components';
import {
  getBetaAvailabilityFromLocalStorage,
  setBetaAvailabilityToLocalStorage,
  isBetaForced,
} from './localStorage';
import { BetaVersion, ContainerContext } from './container.context';
import { useShell } from '@/context';
import { v6 } from '@ovh-ux/manager-core-api';
import { Preference } from '@/types/preferences';

export const BETA = 1;

export const ContainerProvider = ({ children }: { children: JSX.Element }) => {
  const shell = useShell();
  const uxPlugin = shell.getPlugin('ux');
  const preferenceKey = 'NAV_RESHUFFLE_BETA_ACCESS';
  const [isLoading, setIsLoading] = useState(true);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatbotReduced, setChatbotReduced] = useState(false);
  const [aiChatbotOpen, setAIChatbotOpen] = useState(false);
  const [aiChatbotReduced, setAIChatbotReduced] = useState(false);
  const [application, setApplication] = useState<Application>(undefined);
  const [universe, setUniverse] = useState<string>();

  // 1 for beta, otherwise null if not a beta tester
  const [betaVersion, setBetaVersion] = useState<BetaVersion | string>(null);

  // user choice about using or not the beta
  const [useBeta, setUseBeta] = useState(false);

  const [isLivechatEnabled, setIsLivechatEnabled] = useState(false);
  const [isAIChatbotEnabled, setIsAIChatbotEnabled] = useState(false);

  const aiChatbotOpenRef = useRef(aiChatbotOpen);

  const fetchFeatureAvailability = async () => {
    interface CurrentContextAvailability {
      pnr: boolean;
      livechat: boolean;
      'ai-chatbot': boolean;
    }
    const getBetaVersion = (value: CurrentContextAvailability) => {
      if (value.pnr) {
        return BETA;
      }
      return null;
    };

    return fetchFeatureAvailabilityData(['livechat', 'pnr', 'ai-chatbot'])
      .then((value) => ({
        version: getBetaVersion(value),
        livechat: !!value.livechat,
        aiChatbot: !!value['ai-chatbot'],
      }))
      .catch(() => ({
        version: '',
        livechat: false,
        aiChatbot: false,
      }));
  };

  const fetchBetaChoice = async () => {
    const betaValue = getBetaAvailabilityFromLocalStorage();
    const fetchPromise = betaValue
      ? Promise.resolve({ value: betaValue })
      : (v6.get<Preference>(
          `/me/preferences/manager/${preferenceKey}`,
        ).then(({ data }) => data) as Promise<{ value: string }>);

    return fetchPromise
      .then(({ value }) => {
        setUseBeta(value === 'true');
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          v6.post(`/me/preferences/manager`, {
            key: preferenceKey,
            value: 'false',
          }).catch(() => {});
          setUseBeta(false);
        } else {
          throw error;
        }
      });
  };

  const updateBetaChoice = async (accept = false) => {
    const updatePromise = isBetaForced()
      ? Promise.resolve(setBetaAvailabilityToLocalStorage(accept))
      : v6.put(`/me/preferences/manager/${preferenceKey}`, {
          key: preferenceKey,
          value: accept.toString(),
        }).catch(() => {});

    return updatePromise.then(() => {
      window.location.reload();
    });
  };

  const createBetaChoice = async (accept = false) => {
    const createPromise = isBetaForced()
      ? Promise.resolve(setBetaAvailabilityToLocalStorage(accept))
      : v6.post(`/me/preferences/manager`, {
          key: preferenceKey,
          value: accept.toString(),
        }).catch(() => {});

    return createPromise.then(() => {
      window.location.reload();
    });
  };

  /**
   * Initialisation is done here, we check if the user is a beta tester,
   * then we check his preferences regarding using or not the beta version.
   * If he has not preferences saved, we set askBeta to true in order
   * to provide him the choice.
   */
  useEffect(() => {
    fetchFeatureAvailability()
      .then(({ version, livechat, aiChatbot }) => {
        setBetaVersion(version);
        setIsLivechatEnabled(livechat);
        setIsAIChatbotEnabled(aiChatbot);

        if (version) {
          return fetchBetaChoice();
        }

        return null;
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    aiChatbotOpenRef.current = aiChatbotOpen;
  }, [aiChatbotOpen]);

  useEffect(() => {
    uxPlugin.onChatbotVisibilityChange(async () => {
      const chatbotVisibility = await uxPlugin.isChatbotVisible();
      if (isLivechatEnabled) {
        setChatbotOpen(chatbotVisibility);
        setChatbotReduced(false);
      }
      if (chatbotVisibility && aiChatbotOpenRef.current) {
        setAIChatbotOpen(false);
      }
    });
  }, [isLivechatEnabled]);

  useEffect(() => {
    // special HPC case
    if (window.location.hash?.endsWith('hosted-private-cloud')) {
      return;
    }
    if (application?.universe) {
      setUniverse(application.universe);
    }
  }, [application]);

  useEffect(() => {
    shell.getPlugin('environment').onUniverseChange((universe: string) => {
      setUniverse(universe);
    });
  }, []);

  const containerContext = {
    createBetaChoice,
    betaVersion,
    useBeta,
    isLivechatEnabled,
    isLoading,
    updateBetaChoice,
    chatbotOpen,
    setChatbotOpen,
    chatbotReduced,
    setChatbotReduced,
    aiChatbotOpen,
    setAIChatbotOpen,
    aiChatbotReduced,
    setAIChatbotReduced,
    isAIChatbotEnabled,
    application,
    setApplication,
    universe: universe || application?.universe,
    setUniverse(universe: string) {
      shell.getPlugin('environment').setUniverse(universe);
    },
  };

  return (
    <ContainerContext.Provider value={containerContext}>
      {children}
    </ContainerContext.Provider>
  );
};

export default ContainerProvider;
